"use client";

import { LeadThrough, TestDriveRequestStatus } from "@alba-cars/common-modules";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { DatePicker } from "@/components/common/date/DatePicker";
import FormPhoneField from "@/components/common/forms/FormPhoneField";
import FormTextField from "@/components/common/forms/FormTextField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from "@/i18n/routing";
import { createTestDriveRequest } from "@/lib/api/test-drive-request/create-test-drive-request";
import { cn } from "@/lib/utils";
import { readUTMCookies } from "@/lib/utm-tracker";
import { TestDriveFormData, createTestDriveSchema } from "@/schemas/validation/testDriveFormSchema";

interface TestDriveModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
}

// ─── Dubai Time Helpers ───────────────────────────────────────────────────────

const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000; // UTC+4

const getDubaiNow = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utc + DUBAI_OFFSET_MS);
};

// ─── Time Slot Helpers ────────────────────────────────────────────────────────

/** 9:30 am through 9:30 pm: 9:30 am, hourly 10:00 am–9:00 pm, then 9:30 pm */
const generateTimeSlots = () => {
  const slots: string[] = ["9:30 am"];
  for (let hour = 10; hour <= 21; hour++) {
    const normalizedHour = hour % 24;
    const formattedHour = normalizedHour === 0 ? 12 : normalizedHour <= 12 ? normalizedHour : normalizedHour - 12;
    const amPm = normalizedHour < 12 ? "am" : "pm";
    slots.push(`${formattedHour}:00 ${amPm}`);
  }
  slots.push("9:30 pm");
  return slots;
};

const isCurrentlyLateNight = () => {
  const dubaiNow = getDubaiNow();
  return dubaiNow.getHours() >= 21;
};

const isLateNightSlot = (timeSlot: string) => {
  const [hourStr, , amPm] = timeSlot.split(/[: ]/);
  const hour = parseInt(hourStr, 10);
  return (amPm.toLowerCase() === "pm" && hour >= 9) || (amPm.toLowerCase() === "am" && hour === 12);
};

const isTimeSlotDisabled = (timeSlot: string, selectedDate: Date) => {
  if (timeSlot === "12:00 am") return true;

  const dubaiNow = getDubaiNow();
  const todayDubai = new Date(dubaiNow.getFullYear(), dubaiNow.getMonth(), dubaiNow.getDate());
  const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

  if (selectedDay.getTime() > todayDubai.getTime()) return false;

  if (selectedDay.getTime() === todayDubai.getTime()) {
    if (isCurrentlyLateNight() && isLateNightSlot(timeSlot)) return true;

    const [hourStr, minuteStr, amPm] = timeSlot.split(/[: ]/);
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (amPm.toLowerCase() === "pm" && hour < 12) hour += 12;
    else if (amPm.toLowerCase() === "am" && hour === 12) hour = 0;

    const slotTimeDubai = new Date(dubaiNow);
    slotTimeDubai.setHours(hour, minute, 0, 0);

    const twoHoursFromNow = dubaiNow.getTime() + 2 * 60 * 60 * 1000;
    return slotTimeDubai.getTime() <= twoHoursFromNow;
  }

  return false;
};

const updateDateWithTime = (date: string, timeSlot: string) => {
  const [hourStr, minuteStr, amPm] = timeSlot.split(/[: ]/);
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (amPm.toLowerCase() === "pm" && hour < 12) hour += 12;
  else if (amPm.toLowerCase() === "am" && hour === 12) hour = 0;

  const selectedDate = new Date(date);

  // Build a Dubai local time string and convert to UTC
  // Dubai is UTC+4, so we subtract 4 hours to get UTC
  const dubaiAsUTC = Date.UTC(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    hour,
    minute,
    0,
    0,
  );

  // Subtract Dubai offset to get real UTC
  return new Date(dubaiAsUTC - DUBAI_OFFSET_MS).toISOString();
};
const timeSlots = generateTimeSlots();

// ─────────────────────────────────────────────────────────────────────────────

const TestDriveModal = ({ isOpen, onClose, vehicleId }: TestDriveModalProps) => {
  const t = useTranslations("CarDetails.testDriveModal");
  const router = useRouter();
  const { isAuthLoading, isAuthenticated, user } = useAuth();

  const schema = useMemo(() => createTestDriveSchema(t), [t]);

  const handleClose = () => {
    onClose();
  };

  const { control, handleSubmit, reset, watch, setValue } = useForm<TestDriveFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      vehicle: { existingId: vehicleId },
      status: TestDriveRequestStatus.NOT_CONTACTED,
      leadThrough: LeadThrough.WEBSITE,
      requestedDate: new Date().toISOString(),
      selectedTime: timeSlots[0],
      marketing: undefined,
    },
  });

  const firstAvailableSlotRef = useRef<HTMLButtonElement>(null);
  const selectedSlotRef = useRef<HTMLButtonElement>(null);
  const prevCalendarDayKeyRef = useRef<string | null>(null);

  const resetForm = useCallback(() => {
    const dubaiNow = getDubaiNow();
    const firstValidSlot = timeSlots.find((slot) => !isTimeSlotDisabled(slot, dubaiNow)) || timeSlots[0];
    const marketingData = readUTMCookies();
    reset({
      vehicle: { existingId: vehicleId },
      status: TestDriveRequestStatus.NOT_CONTACTED,
      leadThrough: LeadThrough.WEBSITE,
      requestedDate: updateDateWithTime(dubaiNow.toISOString(), firstValidSlot),
      selectedTime: firstValidSlot,
      testerName: isAuthenticated && user ? user.name || "" : "",
      testerMobile: isAuthenticated && user ? user.phone || "" : "",
      ...(Object.keys(marketingData).length > 0 ? { marketing: marketingData } : {}),
    });
  }, [isAuthenticated, user, vehicleId, reset]);

  const requestedDate = watch("requestedDate");
  const selectedTime = watch("selectedTime");

  const showLateNightNotice = (() => {
    const dubaiNow = getDubaiNow();
    const todayDubai = new Date(dubaiNow.getFullYear(), dubaiNow.getMonth(), dubaiNow.getDate());
    const selectedDay = new Date(
      new Date(requestedDate).getFullYear(),
      new Date(requestedDate).getMonth(),
      new Date(requestedDate).getDate(),
    );
    return isCurrentlyLateNight() && selectedDay.getTime() === todayDubai.getTime();
  })();

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen) prevCalendarDayKeyRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !requestedDate) return;

    const d = new Date(requestedDate);
    const calendarDayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

    const timer = setTimeout(() => {
      if (prevCalendarDayKeyRef.current !== calendarDayKey) {
        firstAvailableSlotRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        prevCalendarDayKeyRef.current = calendarDayKey;
      } else {
        selectedSlotRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [isOpen, requestedDate, selectedTime]);

  const createTestDriveRequestMutation = useMutation({
    mutationFn: async (data: TestDriveFormData) => await createTestDriveRequest(data),
    onSuccess: (_, { testerMobile, testerEmail }) => {
      const token = `td_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem("testDriveToken", token);
      sessionStorage.setItem("testDriveData", JSON.stringify({ testerMobile, testerEmail }));
      handleClose();
      router.push("/thank-you/test-drive?ref=success");
    },
    onError: (_error) => {
      toast.error(_error.message?.toString() ?? t("errorSubmitting"));
    },
  });

  const onSubmit = async (data: TestDriveFormData) => {
    await createTestDriveRequestMutation.mutateAsync(data);
  };

  const dubaiNow = getDubaiNow();
  const thirtyDaysFromNow = new Date(dubaiNow);
  thirtyDaysFromNow.setDate(dubaiNow.getDate() + 30);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="no-scrollbar left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 bg-white p-6 text-black sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[95vh] sm:w-[95%] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:border sm:p-6 [&>button>svg]:h-6 [&>button>svg]:w-6 [&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-gray-100 [&>button]:opacity-100 sm:[&>button]:bg-transparent">
        <DialogHeader className="mb-4 mt-0 text-left sm:mt-0 sm:text-left">
          <DialogTitle className="urbanist text-2xl font-semibold text-black sm:text-3xl rtl:text-right">
            {t("title")}
          </DialogTitle>
          <p className="inter text-xs text-gray-600 sm:text-sm rtl:text-right">{t("subtitle")}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormTextField
            name="testerName"
            control={control}
            placeholder={isAuthLoading ? t("loadingUserInfo") : t("namePlaceholder")}
            disabled={isAuthLoading || isAuthenticated}
            rules={{
              required: t("nameRequired"),
              minLength: { value: 2, message: t("nameMinLength") },
              maxLength: { value: 255, message: t("nameMaxLength") },
            }}
          />

          <FormPhoneField
            name="testerMobile"
            control={control}
            placeholder={isAuthLoading ? t("loadingUserInfo") : t("mobilePlaceholder")}
            disabled={isAuthLoading || isAuthenticated}
            className="rtl:placeholder:text-right"
            rules={{
              required: t("mobileRequired"),
              minLength: { value: 8, message: t("mobileMinLength") },
              maxLength: { value: 20, message: t("mobileMaxLength") },
            }}
          />

          <div className="space-y-2">
            <span className="inter text-sm font-bold text-primary-foreground">{t("selectDateTime")}</span>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="col-span-2">
                <Controller
                  name="requestedDate"
                  control={control}
                  rules={{ required: t("dateRequired") }}
                  render={({ field }) => (
                    <DatePicker
                      variant="inline"
                      selectedDate={field.value ? new Date(field.value) : dubaiNow}
                      onChange={(date) => {
                        field.onChange(date?.toISOString());
                      }}
                      minDate={dubaiNow}
                      maxDate={thirtyDaysFromNow}
                    />
                  )}
                />
              </div>

              <div className="flex-1">
                <Controller
                  name="requestedDate"
                  control={control}
                  render={({ field }) => (
                    <p className="mb-2 text-sm font-bold text-gray-900">
                      {new Date(field.value).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  )}
                />

                {/* <p className="font-small inter mb-1 text-sm">{t("timeAvailable")}</p> */}
                <p className="inter mb-3 text-xs"> {t("dubaiTimeNote")}</p>
                {showLateNightNotice && (
                  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    ⚠️ Slots from 9:00 PM to 10:00 PM are unavailable for today. Please select a time for tomorrow or
                    choose another date.
                  </div>
                )}

                <div className="no-scrollbar mt-4 max-h-[200px] overflow-y-auto md:grid md:grid-cols-1 md:gap-1.5">
                  <Controller
                    name="selectedTime"
                    control={control}
                    rules={{ required: t("timeRequired") }}
                    render={({ field }) => {
                      const currentDate = new Date(requestedDate);
                      const firstAvailableIndex = timeSlots.findIndex((slot) => !isTimeSlotDisabled(slot, currentDate));
                      return (
                        <div className="flex gap-2 md:flex-col md:gap-3">
                          {timeSlots.map((time, index) => {
                            const isDisabled = isTimeSlotDisabled(time, currentDate);
                            const isFirstAvailable = index === firstAvailableIndex;
                            return (
                              <button
                                key={time}
                                type="button"
                                ref={(el) => {
                                  if (isFirstAvailable) firstAvailableSlotRef.current = el;
                                  if (field.value === time) selectedSlotRef.current = el;
                                }}
                                onClick={() => {
                                  if (!isDisabled) {
                                    field.onChange(time);
                                    setValue("requestedDate", updateDateWithTime(requestedDate, time));
                                  }
                                }}
                                disabled={isDisabled}
                                dir="ltr"
                                className={cn(
                                  "w-full border",
                                  "rounded-lg px-3 py-1.5 text-center text-sm font-medium transition-all duration-200",
                                  isDisabled
                                    ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                    : field.value === time
                                      ? "cursor-pointer bg-primary text-white hover:bg-primary-dark"
                                      : "cursor-pointer text-primary hover:bg-gray-50 hover:text-primary-dark",
                                )}
                              >
                                {time}
                              </button>
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="inter mt-4 w-full rounded-lg bg-primary py-3 text-center text-sm font-semibold text-white transition-all duration-300 hover:cursor-pointer hover:bg-primary-dark disabled:opacity-50"
            disabled={createTestDriveRequestMutation.isPending}
          >
            {createTestDriveRequestMutation.isPending ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("loading")}
              </div>
            ) : (
              t("submit")
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TestDriveModal;
